import classNames from 'classnames';
import React from 'react';

import {UI_REGION} from '@udemy/browse-utils';
import {LocalizedHtml, useI18n} from '@udemy/i18n';
import {NoticeItem, PersonalPlanLandingBannerNoticeData} from '@udemy/notices';
import {Image} from '@udemy/react-core-components';
import {
    interpolateSubscriptionNoticeData,
    PlanPeriodStyle,
    SubscriptionDiscountRibbon,
} from '@udemy/subscription-browse';
import {ConsumerSubscriptionPlan, SubscriptionPlanContext} from '@udemy/subscription-graphql';
import {udLink, useFormatCurrency, useUDData} from '@udemy/ud-data';

import {PersonalPlanLandingPageProps} from '../../personal-plan-landing-page-body.react-component';
import {
    useNonCatalogCheckoutSource,
    usePersonalPlanSubscriptionCheckoutEntry,
} from '../../personal-plan-subscription-checkout-entry-context';
import {PersonalPlanSubscriptionCTA} from '../personal-plan-subscription-cta/personal-plan-subscription-cta.react-component';
import {NoPlanAlertBanner} from './no-plan-alert-banner.react-component';
import styles from './personal-plan-banner.module.less';

export interface PersonalPlanBannerProps extends PersonalPlanLandingPageProps {
    notice?: NoticeItem<PersonalPlanLandingBannerNoticeData>;
    plan?: SubscriptionPlanContext<ConsumerSubscriptionPlan>;
}

export const PersonalPlanBanner = React.forwardRef<HTMLDivElement, PersonalPlanBannerProps>(
    ({plan, notice: wrappedNotice, isPPLPRevamp}: PersonalPlanBannerProps, ref) => {
        let notice = wrappedNotice?.data;
        const subscriptionCheckoutEntry = usePersonalPlanSubscriptionCheckoutEntry();
        const {source: nonCatalogSource, noticeDataLoading: nonCatalogNoticeDataLoading} =
            useNonCatalogCheckoutSource();
        const {gettext} = useI18n();
        const {request} = useUDData();
        const currencyFormatter = useFormatCurrency();
        const getImageSrc = (imgUrl: string) => {
            return udLink.toStorageStaticAsset(`consumer-subscription/${imgUrl}`);
        };

        const isLoading = plan?.isLoading;
        const isSubscriber = plan && 'isSubscriber' in plan && plan?.isSubscriber;

        const subscriptionPlan = plan && 'id' in plan ? plan : undefined;
        const subscriptionDiscount = subscriptionPlan?.selectedDiscount;
        if (subscriptionPlan && notice) {
            notice = interpolateSubscriptionNoticeData(
                subscriptionPlan,
                notice,
                currencyFormatter,
            ) as PersonalPlanLandingBannerNoticeData;
        }
        const showNoPlanAlert = !isLoading && !isSubscriber && !subscriptionPlan;

        const PPLPRevampBannerDesktop =
            'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-banner/PersonalPlanHeroDesktop.png';
        const PPLPRevampBannerMobile =
            'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-banner/PersonalPlanHeroMobile.png';

        const responsiveImageSrc =
            notice?.image_responsive ??
            (isPPLPRevamp ? PPLPRevampBannerMobile : getImageSrc('billboard/mobile-v5.png'));

        const desktopImageSrc =
            notice?.image ??
            (isPPLPRevamp ? PPLPRevampBannerDesktop : getImageSrc('billboard/desktop-v5.png'));

        return (
            <div
                className={classNames(styles['personal-plan-banner-wrapper'], {
                    [styles.revamp]: isPPLPRevamp,
                    [styles['revamp-with-promotion']]: isPPLPRevamp && notice,
                })}
                ref={ref}
            >
                {showNoPlanAlert && (
                    <div className={styles['no-plan-alert-container']}>
                        <NoPlanAlertBanner />
                    </div>
                )}

                <div className={styles['pp-banner']}>
                    <div className={styles['pp-banner-content']}>
                        <div className={styles['text-container']}>
                            <div
                                className={classNames(
                                    !isPPLPRevamp && 'ud-heading-serif-lg',
                                    styles['product-logo-in-text'],
                                )}
                            >
                                {isPPLPRevamp ? gettext('PERSONAL PLAN') : gettext('Personal Plan')}
                                {subscriptionDiscount && (
                                    <SubscriptionDiscountRibbon
                                        subscriptionDiscount={subscriptionDiscount}
                                        className={styles['discount-ribbon']}
                                    />
                                )}
                            </div>
                            <h1
                                className={classNames(
                                    !isPPLPRevamp && 'ud-heading-serif-xxl',
                                    styles['title'],
                                )}
                                data-purpose="title"
                            >
                                <LocalizedHtml
                                    html={
                                        notice?.title ??
                                        (isPPLPRevamp
                                            ? gettext('Your career companion <br /> for the AI age')
                                            : gettext('Take your career <br />to the next level'))
                                    }
                                    interpolate={{}}
                                />
                            </h1>
                            <div
                                className={classNames('ud-text-lg', styles['subtitle'])}
                                data-purpose="subtitle"
                            >
                                {notice?.subtitle ??
                                    (isPPLPRevamp
                                        ? gettext(
                                              'Future-proof your skills with fresh content from real-world experts, learn AI, prepare for certification exams, and practice your skills with personalized learning.',
                                          )
                                        : gettext(
                                              'Go further at work and in life with subscription access to a collection of top-rated courses in tech, business, and more.',
                                          ))}
                            </div>
                            <PersonalPlanSubscriptionCTA
                                className={styles['subscription-cta']}
                                subtitleClassName={notice?.tagline ? styles['tagline-hidden'] : ''}
                                plan={plan}
                                data-purpose="subscription-cta"
                                uiRegion={UI_REGION.PERSONAL_PLAN_BANNER}
                                planPeriodStyle={
                                    request.isMobile
                                        ? PlanPeriodStyle.MULTILINE_NO_MARGIN
                                        : PlanPeriodStyle.SINGLE
                                }
                                noticeData={notice}
                                source={nonCatalogSource}
                                noticeDataLoading={nonCatalogNoticeDataLoading}
                                subscriptionCheckoutEntry={subscriptionCheckoutEntry}
                            />
                            {notice?.tagline && (
                                <div className={styles['tagline-wide']}>{notice?.tagline}</div>
                            )}
                        </div>
                    </div>

                    <div className={styles['pp-banner-images']}>
                        <Image
                            className={styles['pp-mobile-image']}
                            src={responsiveImageSrc}
                            alt=""
                            width="unset"
                            height="unset"
                        />
                        <Image
                            className={styles['pp-desktop-image']}
                            src={desktopImageSrc}
                            alt=""
                            width={830}
                            height={500}
                        />
                    </div>
                </div>
            </div>
        );
    },
);
